import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useApiClient } from '../../hooks/useApiClient';
import { useToast } from '../../contexts/ToastContext';
import { Notification } from '@haccp/api-client';

type TabType = 'unread' | 'history';

// Icons for notification types
function NotificationIcon({ type }: { type: string }) {
  if (type.includes('TEMP')) {
    return (
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-accent-red/20 to-accent-red/10 flex items-center justify-center">
        <svg className="w-5 h-5 text-accent-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
    );
  }
  if (type.includes('INCIDENT')) {
    return (
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-accent-orange/20 to-accent-orange/10 flex items-center justify-center">
        <svg className="w-5 h-5 text-accent-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
    );
  }
  if (type.includes('CLEANING')) {
    return (
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-accent-yellow/20 to-accent-yellow/10 flex items-center justify-center">
        <svg className="w-5 h-5 text-accent-yellow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
        </svg>
      </div>
    );
  }
  if (type.includes('SUBSCRIPTION')) {
    return (
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-100 to-primary-50 flex items-center justify-center">
        <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      </div>
    );
  }
  // Default bell icon
  return (
    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-100 to-primary-50 flex items-center justify-center">
      <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
    </div>
  );
}

function formatDate(date: Date | string, language: string, t: (key: string, opts?: any) => string) {
  const d = new Date(date);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return t('notifications.justNow');
  if (diffMins < 60) return t('notifications.minutesAgo', { count: diffMins });
  if (diffHours < 24) return t('notifications.hoursAgo', { count: diffHours });
  if (diffDays < 7) return t('notifications.daysAgo', { count: diffDays });

  return d.toLocaleDateString(language, {
    day: 'numeric',
    month: 'short',
    year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

export default function NotificationsPage() {
  const { t, i18n } = useTranslation();
  const apiClient = useApiClient();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabType>('unread');

  // Notification type translations — resolved at render time via t()
  const notificationTypeLabels: Record<string, string> = {
    TEMP_OUT_OF_RANGE: t('notifications.types.tempOutOfRange'),
    INCIDENT_OPENED: t('notifications.types.incidentOpened'),
    INCIDENT_CLOSED: t('notifications.types.incidentClosed'),
    CLEANING_OVERDUE: t('notifications.types.cleaningOverdue'),
    SUBSCRIPTION_EXPIRING: t('notifications.types.subscriptionExpiring'),
    WELCOME: t('notifications.types.welcome'),
    SYSTEM: t('notifications.types.system'),
  };

  // Auto-refresh interval (15 seconds for notifications - more frequent)
  const REFRESH_INTERVAL = 15 * 1000;

  // Fetch unread notifications
  const { data: notifications, isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      try {
        return await apiClient.getNotifications();
      } catch {
        return [];
      }
    },
    refetchInterval: REFRESH_INTERVAL,
  });

  // Fetch notification history
  const { data: historyNotifications, isLoading: historyLoading } = useQuery({
    queryKey: ['notifications-history'],
    queryFn: async () => {
      try {
        return await apiClient.getNotificationHistory();
      } catch {
        return [];
      }
    },
    refetchInterval: REFRESH_INTERVAL * 2, // Less frequent for history
  });

  // Mark as read mutation
  const markReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      return apiClient.markNotificationRead(notificationId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-history'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: () => {
      addToast(t('notifications.markReadError'), 'error');
    },
  });

  // Mark all as read
  const markAllAsRead = async () => {
    const unreadNotifications = (notifications || []).filter(
      (n: Notification) => n.status === 'PENDING' || n.status === 'SENT'
    );

    for (const notif of unreadNotifications) {
      await markReadMutation.mutateAsync(notif.id);
    }
    addToast(t('notifications.allMarkedRead'), 'success');
  };

  const notificationsList = (notifications || []) as Notification[];
  const historyList = (historyNotifications || []) as Notification[];
  const unreadCount = notificationsList.filter(
    (n: Notification) => n.status === 'PENDING' || n.status === 'SENT'
  ).length;

  const displayList = activeTab === 'unread' ? notificationsList : historyList;
  const isLoadingCurrent = activeTab === 'unread' ? isLoading : historyLoading;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-brand-dark to-primary-600 bg-clip-text text-transparent">
            {t('notifications.title')}
          </h1>
          <p className="text-neutral-600 mt-1">
            {unreadCount > 0
              ? t('notifications.unreadCount', { count: unreadCount })
              : t('notifications.noNewNotifications')}
          </p>
        </div>
        {unreadCount > 0 && activeTab === 'unread' && (
          <button
            onClick={markAllAsRead}
            disabled={markReadMutation.isPending}
            className="w-full sm:w-auto px-4 py-2 rounded-lg bg-gradient-to-r from-primary-500 to-primary-600 text-white font-medium hover:from-primary-600 hover:to-primary-700 transition-all shadow-md hover:shadow-lg disabled:opacity-50"
          >
            {t('notifications.markAllRead')}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-neutral-200 overflow-x-auto whitespace-nowrap">
        <button
          onClick={() => setActiveTab('unread')}
          className={`px-6 py-3 font-medium text-sm transition-all relative ${
            activeTab === 'unread'
              ? 'text-primary-600'
              : 'text-neutral-500 hover:text-neutral-700'
          }`}
        >
          <span className="flex items-center gap-2">
            {t('notifications.tabUnread')}
            {unreadCount > 0 && (
              <span className="px-2 py-0.5 text-xs font-bold bg-gradient-to-r from-accent-red to-accent-red/80 text-white rounded-full">
                {unreadCount}
              </span>
            )}
          </span>
          {activeTab === 'unread' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-500 to-primary-600"></div>
          )}
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-6 py-3 font-medium text-sm transition-all relative ${
            activeTab === 'history'
              ? 'text-primary-600'
              : 'text-neutral-500 hover:text-neutral-700'
          }`}
        >
          <span className="flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {t('notifications.tabHistory')}
          </span>
          {activeTab === 'history' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-500 to-primary-600"></div>
          )}
        </button>
      </div>

      {/* Notifications List */}
      <div className="bg-white rounded-xl border border-neutral-200 shadow-lg overflow-hidden">
        {isLoadingCurrent ? (
          <div className="p-4 sm:p-8 text-center text-neutral-500">
            <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            {t('notifications.loading')}
          </div>
        ) : displayList.length === 0 ? (
          <div className="p-6 sm:p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-100 to-primary-50 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">
              {activeTab === 'unread' ? t('notifications.noUnread') : t('notifications.noNotifications')}
            </h3>
            <p className="text-neutral-500">
              {activeTab === 'unread'
                ? t('notifications.allCaughtUp')
                : t('notifications.noNotificationsYet')}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-neutral-100">
            {displayList.map((notification: Notification) => {
              const isUnread = notification.status === 'PENDING' || notification.status === 'SENT';

              return (
                <div
                  key={notification.id}
                  className={`p-4 flex flex-col sm:flex-row sm:items-start gap-4 transition-colors ${
                    isUnread
                      ? 'bg-gradient-to-r from-primary-50/50 to-transparent hover:from-primary-50'
                      : 'hover:bg-neutral-50'
                  }`}
                >
                  <NotificationIcon type={notification.type} />

                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                      <div>
                        <h4 className={`font-semibold ${isUnread ? 'text-neutral-900' : 'text-neutral-700'}`}>
                          {notificationTypeLabels[notification.type] || notification.titleKey}
                        </h4>
                        <p className="text-sm text-neutral-600 mt-0.5">
                          {notification.bodyKey}
                        </p>
                      </div>
                      <span className="text-xs text-neutral-400 whitespace-nowrap">
                        {formatDate(notification.createdAt, i18n.language, t)}
                      </span>
                    </div>
                  </div>

                  {isUnread && (
                    <button
                      onClick={() => markReadMutation.mutate(notification.id)}
                      disabled={markReadMutation.isPending}
                      className="flex-shrink-0 p-2 text-neutral-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                      title={t('notifications.markRead')}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </button>
                  )}

                  {!isUnread && (
                    <div className="flex-shrink-0 p-2 text-accent-green">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
